// Pulse / Pipeline section — quote funnel.
//
// Visual: a "stacked horizontal bar" funnel substituting for a pie
// chart (no third-party charting lib per the locked design). Each
// segment's width is proportional to its share of total resolved
// quotes (accepted + declined + expired). Plus a conversion-rate
// callout + recent activity tail.

import React, { useContext } from 'react';
import {
  Box,
  ButtonBase,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import SectionShell from './SectionShell';
import KpiTile from './KpiTile';
import { fmtUsd } from '../demoData';
import { BusinessContext } from '../../../contexts/BusinessContext';

const ACTION_COLOR = {
  accepted: 'success.main',
  declined: 'error.main',
  expired: 'text.disabled',
  viewed: 'info.main',
  sent: 'primary.main',
};

function formatRelative(iso) {
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const mins = Math.round(diff / 60000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.round(hrs / 24)}d`;
}

export default function PipelineSection({ pipeline }) {
  const { business } = useContext(BusinessContext);
  const navigate = useNavigate();
  const basePath = business?.slug ? `/business/${business.slug}` : '';
  const quotePath = (state) =>
    state ? `${basePath}/quotes?state=${state}` : `${basePath}/quotes`;

  if (!pipeline) {
    return (
      <SectionShell
        title="Pipeline"
        subtitle="Estimate funnel"
        icon={<RocketLaunchOutlinedIcon />}
        empty
        emptyCopy="Send your first estimate to start tracking conversion."
      />
    );
  }

  const open = pipeline.draftCount + pipeline.sentCount + pipeline.viewedCount;
  const resolved =
    pipeline.acceptedCount + pipeline.declinedCount + pipeline.expiredCount;
  const total = open + resolved;

  return (
    <SectionShell
      title="Pipeline"
      subtitle="Estimate funnel last 30 days"
      icon={<RocketLaunchOutlinedIcon />}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 2,
        }}
      >
        <ButtonBase
          component={RouterLink}
          to={quotePath('accepted')}
          sx={{ display: 'block', textAlign: 'left', borderRadius: 2 }}
        >
          <KpiTile
            value={`${pipeline.conversionPct}%`}
            label="Conversion rate"
            delta={pipeline.conversionDeltaPct}
            accent="success.main"
          />
        </ButtonBase>
        <ButtonBase
          component={RouterLink}
          to={quotePath()}
          sx={{ display: 'block', textAlign: 'left', borderRadius: 2 }}
        >
          <KpiTile
            value={fmtUsd(pipeline.avgQuoteValueCents)}
            label="Avg estimate value"
            sublabel={`${open} open · ${resolved} resolved`}
          />
        </ButtonBase>
      </Box>

      {/* Funnel stages as a stacked bar */}
      <Box>
        <Box
          sx={{
            display: 'flex',
            height: 10,
            borderRadius: 999,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {[
            { key: 'draft', count: pipeline.draftCount, color: 'grey.400' },
            { key: 'sent', count: pipeline.sentCount, color: 'primary.light' },
            { key: 'viewed', count: pipeline.viewedCount, color: 'info.main' },
            {
              key: 'accepted',
              count: pipeline.acceptedCount,
              color: 'success.main',
            },
            {
              key: 'declined',
              count: pipeline.declinedCount,
              color: 'error.light',
            },
            {
              key: 'expired',
              count: pipeline.expiredCount,
              color: 'warning.light',
            },
          ]
            .filter((s) => s.count > 0)
            .map((s) => (
              <Box
                key={s.key}
                title={`${s.key} (${s.count})`}
                sx={{
                  flex: s.count,
                  bgcolor: s.color,
                  transition: 'flex 0.2s',
                }}
              />
            ))}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.25,
            mt: 1.5,
            justifyContent: 'flex-start',
          }}
        >
          {[
            { key: 'Draft', count: pipeline.draftCount, color: 'grey.400' },
            { key: 'Sent', count: pipeline.sentCount, color: 'primary.light' },
            { key: 'Viewed', count: pipeline.viewedCount, color: 'info.main' },
            {
              key: 'Accepted',
              count: pipeline.acceptedCount,
              color: 'success.main',
            },
            {
              key: 'Declined',
              count: pipeline.declinedCount,
              color: 'error.light',
            },
            {
              key: 'Expired',
              count: pipeline.expiredCount,
              color: 'warning.light',
            },
          ].map((s) => (
            <ButtonBase
              key={s.key}
              component={RouterLink}
              to={quotePath(s.key.toLowerCase())}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                borderRadius: 1,
                px: 0.5,
                py: 0.25,
                '&:hover': { backgroundColor: 'action.hover' },
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: 999,
                  bgcolor: s.color,
                }}
              />
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontWeight: 500 }}
              >
                {s.key} {s.count}
              </Typography>
            </ButtonBase>
          ))}
        </Box>
      </Box>

      <Divider sx={{ mt: 0.5 }} />

      <Box>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            display: 'block',
            mb: 1,
          }}
        >
          Recent activity
        </Typography>
        <Stack divider={<Divider />} spacing={0}>
          {pipeline.recentActivity.slice(0, 4).map((q) => (
            <Box
              key={q.id}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`${basePath}/quotes/${q.id}`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(`${basePath}/quotes/${q.id}`);
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                py: 1,
                cursor: 'pointer',
                borderRadius: 1,
                '&:hover': { backgroundColor: 'action.hover' },
              }}
            >
              <Box
                sx={{
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                }}
              >
                <Chip
                  label={q.action}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 20,
                    fontSize: 10,
                    textTransform: 'uppercase',
                    fontWeight: 700,
                    color: ACTION_COLOR[q.action] || 'text.primary',
                    borderColor: ACTION_COLOR[q.action] || 'divider',
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: 'text.primary',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {q.customerName}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary' }}
                  >
                    {q.quoteNumber} · {formatRelative(q.at)} ago
                  </Typography>
                </Box>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  fontVariantNumeric: 'tabular-nums',
                  whiteSpace: 'nowrap',
                }}
              >
                {fmtUsd(q.amountCents)}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>
    </SectionShell>
  );
}
